import { filter } from 'lodash-es';
import { useMemo } from 'react';
import type {
	DefaultContext,
	Incident,
	ISecodaEntity,
	UpdateRequestParams,
} from '../../api';
import { useUpdateIncident } from '../../api';
import type { ExtendedDataTableColumn } from '../../components/TableV2/types';

import type { MetricType } from '@repo/api-codegen';
import { formatMonitorMetricValue } from '@repo/common/utils';
import type { UpdateModelHook } from '../../api/factories/types';
import { useGenericColumns } from '../../components/TableV2/SecodaEntity.hooks';
import {
	BoldTextRender,
	MonitorResourceRender,
	RelativeTimeRender,
} from '../../components/TableV2/render';
import { IncidentStatusBadge } from '../MonitorPage/components/IncidentStatusBadge';

export function useColumns<T extends Incident>(): ExtendedDataTableColumn<T>[] {
	const genericColumns = useGenericColumns({
		useUpdate: useUpdateIncident as unknown as UpdateModelHook<
			ISecodaEntity,
			UpdateRequestParams<ISecodaEntity>,
			DefaultContext<ISecodaEntity>,
			unknown
		>,
	}) as ExtendedDataTableColumn<T>[];

	const columns = useMemo(
		() => [
			{
				accessor: 'name',
				titleLabel: 'Name',
				render: (record) => <BoldTextRender record={record} field={'name'} />,
			},
			{
				accessor: 'monitor',
				titleLabel: 'Resource',
				render: (record) => <MonitorResourceRender record={record} />,
			},
			{
				accessor: 'status',
				titleLabel: 'Status',
				render: (record) => (
					<IncidentStatusBadge incidentStatus={record.status} />
				),
			},
			{
				accessor: 'value',
				titleLabel: 'Value',
				render: (record: Incident) =>
					formatMonitorMetricValue(
						record.metric_type as MetricType,
						parseFloat(record.value)
					),
			},
			{
				accessor: 'first_seen_at',
				titleLabel: 'Last run',
				render: (record) => (
					<RelativeTimeRender record={record} field={'first_seen_at'} />
				),
			},

			...filter(genericColumns, (column) =>
				['updated_at'].includes(column.accessor)
			),
		],
		[genericColumns]
	);

	return columns;
}
